import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FilterProvider } from './context/FilterContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import PageLoader from './components/PageLoader';
import DogBiteReports from './components/DogBiteReports';
import PetRegisteredView from './components/PetRegisteredView';
import ReportTable from './components/ReportTable';
import { hasAdminManagementAccess, hasCampaignAccess, hasNewsAccess } from './config/adminAccess';


// Lazy load page components to reduce initial bundle size
const Dashboard = lazy(() => import('./components/Dashboard'));
const LiveMap = lazy(() => import('./components/LiveMap'));
const CulledDogView = lazy(() => import('./components/CulledDogView'));
const Users = lazy(() => import('./components/Users'));
const HealthcareUsers = lazy(() => import('./components/HealthcareUsers'));
const DogComplaints = lazy(() => import('./components/DogComplaints'));
const ManholeComplaints = lazy(() => import('./components/ManholeComplaints'));
const AdminManagement = lazy(() => import('./components/AdminManagement'));
const Campaigns = lazy(() => import('./components/Campaigns'));
const News = lazy(() => import('./components/News'));
const UCDataManager = lazy(() => import('./components/UCDataManager'));

// Wrapper component for lazy loaded pages with error boundary
const PageWrapper = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader message="Loading..." />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

// Route protection for campaign management
const CampaignManagementRoute = ({ children }) => {
  const { admin } = useAuth();
  if (!hasCampaignAccess(admin?.email)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Route protection for admin management
const AdminManagementRoute = ({ children }) => {
  const { admin } = useAuth();
  if (!hasAdminManagementAccess(admin?.email)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Route protection for News management
const NewsManagementRoute = ({ children }) => {
  const { admin } = useAuth();
  if (!hasNewsAccess(admin?.email)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <NotificationProvider>
            <FilterProvider>
              <Routes>
                {/* Protected Routes - requires authentication */}
                <Route element={<ProtectedRoute />}>
                  {/* Layout wrapper with sidebar and header */}
                  <Route element={<Layout />}>
                    {/* Dashboard - home page */}
                    <Route
                      path="/"
                      element={
                        <PageWrapper>
                          <Dashboard />
                        </PageWrapper>
                      }
                    />

                    {/* Live Map */}
                    <Route
                      path="/live-map"
                      element={
                        <PageWrapper>
                          <LiveMap />
                        </PageWrapper>
                      }
                    />

                    {/* Culled Dog View */}
                    <Route
                      path="/culled-dog-view"
                      element={
                        <PageWrapper>
                          <CulledDogView />
                        </PageWrapper>
                      }
                    />


                    {/* Pet Registered View */}
                    <Route 
                      path="/pet-registered-view"
                      element={
                        <PageWrapper>
                          <PetRegisteredView />
                        </PageWrapper>
                      }
                    />


                    {/* Users Management */}
                    <Route
                      path="/users"
                      element={
                        <PageWrapper>
                          <Users />
                        </PageWrapper>
                      }
                    />

                     {/* Healthcare Users Management */}
                    <Route
                      path="/health-users"
                      element={
                        <PageWrapper>
                          <HealthcareUsers />
                        </PageWrapper>
                      }
                    />

                    {/* Complaints */}
                    <Route
                      path="/complaints"
                      element={
                        <PageWrapper>
                          <DogComplaints />
                        </PageWrapper>
                      }
                    />
                    {/* Manhole Complaints */}
                    <Route
                      path="/manhole-complaints"
                      element={
                        <PageWrapper>
                          <ManholeComplaints />
                        </PageWrapper>
                      }
                    />
                    {/* Dog Bite Reports */}
                    <Route
                      path="/dog-bite-reports"
                      element={
                        <DogBiteReports />
                      }
                    />

                    {/* Report Table */}
                    <Route
                      path="/report-table"
                      element={
                        <ReportTable />
                      }
                    />

                    {/* Campaigns Management - Authorized emails only */}
                    <Route
                      path="/campaigns"
                      element={
                        <CampaignManagementRoute>
                          <PageWrapper>
                            <Campaigns />
                          </PageWrapper>
                        </CampaignManagementRoute>
                      }
                    />

                    {/* Admin Management - Authorized emails only */}
                    <Route
                      path="/admin-management"
                      element={
                        <AdminManagementRoute>
                          <PageWrapper>
                            <AdminManagement />
                          </PageWrapper>
                        </AdminManagementRoute>
                      }
                    />

                    {/* News Management */}
                    <Route
                      path="/news"
                      element={
                        <NewsManagementRoute>
                          <PageWrapper>
                            <News />
                          </PageWrapper>
                        </NewsManagementRoute>
                      }
                    />

                    {/* UC Data Manager */}
                    <Route
                      path="/uc-data-manager"
                      element={
                        <PageWrapper>
                          <UCDataManager />
                        </PageWrapper>
                      }
                    />
                  </Route>
                </Route>
              </Routes>
            </FilterProvider>
          </NotificationProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
